import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Package, Plus, Minus } from 'lucide-react';

import CurrencyConverter from '../../CurrencyConverter';
import s from './AddonsSelector.css';
import messages from '../../../locale/messages';

class AddonsSelector extends Component {
    static propTypes = {
        addons: PropTypes.array,
        selectedAddons: PropTypes.object, // { addonId: quantity }
        onChange: PropTypes.func.isRequired,
        guests: PropTypes.number,
        currency: PropTypes.string,
    };

    static defaultProps = {
        addons: [],
        selectedAddons: {},
        guests: 1,
    };

    handleToggle = (addon) => {
        const { selectedAddons, onChange, guests } = this.props;
        const addonId = addon.id;

        if (selectedAddons[addonId]) {
            // Remove addon
            const { [addonId]: removed, ...rest } = selectedAddons;
            onChange(rest);
        } else {
            // Add addon with default quantity
            const defaultQuantity = addon.priceType === 'per_person' ? guests : 1;
            onChange({ ...selectedAddons, [addonId]: defaultQuantity });
        }
    };

    handleQuantityChange = (addonId, delta) => {
        const { selectedAddons, onChange, guests } = this.props;
        const currentQty = selectedAddons[addonId] || 1;
        const newQty = Math.max(1, Math.min(currentQty + delta, guests));
        onChange({ ...selectedAddons, [addonId]: newQty });
    };

    calculateAddonTotal = (addon) => {
        const { selectedAddons } = this.props;
        const quantity = selectedAddons[addon.id] || 0;
        if (quantity === 0) return 0;
        return addon.price * quantity;
    };

    calculateTotalAddons = () => {
        const { addons, selectedAddons } = this.props;
        let total = 0;
        addons.forEach(addon => {
            const quantity = selectedAddons[addon.id] || 0;
            total += addon.price * quantity;
        });
        return total;
    };

    render() {
        const { addons, selectedAddons, guests, currency } = this.props;
        const { formatMessage } = this.props.intl;

        if (!addons || addons.length === 0) {
            return null;
        }

        const totalAddons = this.calculateTotalAddons();

        return (
            <div className={s.addonsSection}>
                <div className={s.sectionTitle}>
                    <Package className={s.sectionIcon} />
                    <span><FormattedMessage {...messages.addonsAvailable} /></span>
                </div>

                {addons.map((addon) => {
                    const isSelected = !!selectedAddons[addon.id];
                    const quantity = selectedAddons[addon.id] || 0;

                    return (
                        <div
                            key={addon.id}
                            className={`${s.addonItem} ${isSelected ? s.addonItemSelected : ''}`}
                        >
                            <div className={s.addonLeft}>
                                <input
                                    type="checkbox"
                                    className={s.checkbox}
                                    checked={isSelected}
                                    onChange={() => this.handleToggle(addon)}
                                />
                                <div className={s.addonInfo}>
                                    <div className={s.addonName}>{addon.name}</div>
                                    {addon.description && (
                                        <div className={s.addonDescription}>{addon.description}</div>
                                    )}
                                </div>
                            </div>
                            <div className={s.addonRight}>
                                <div className={s.addonPrice}>
                                    <CurrencyConverter amount={addon.price} from={currency} />
                                    <span className={s.addonPriceType}>
                                        {addon.priceType === 'per_person'
                                            ? ` / ${formatMessage(messages.perPerson).toLowerCase()}`
                                            : ''
                                        }
                                    </span>
                                </div>
                                {isSelected && addon.priceType === 'per_person' && (
                                    <div className={s.quantitySelector}>
                                        <button
                                            type="button"
                                            className={s.quantityBtn}
                                            onClick={() => this.handleQuantityChange(addon.id, -1)}
                                            disabled={quantity <= 1}
                                        >
                                            <Minus size={14} />
                                        </button>
                                        <span className={s.quantityValue}>{quantity}</span>
                                        <button
                                            type="button"
                                            className={s.quantityBtn}
                                            onClick={() => this.handleQuantityChange(addon.id, 1)}
                                            disabled={quantity >= guests}
                                        >
                                            <Plus size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {totalAddons > 0 && (
                    <div className={s.totalRow}>
                        <span className={s.totalLabel}>
                            <FormattedMessage {...messages.addonsTotal} />
                        </span>
                        <span className={s.totalValue}>
                            <CurrencyConverter amount={totalAddons} from={currency} />
                        </span>
                    </div>
                )}
            </div>
        );
    }
}

const mapState = (state) => ({
    currency: state?.currency?.base || 'USD',
});

export default injectIntl(withStyles(s)(connect(mapState)(AddonsSelector)));
